import { useDispatch } from 'react-redux';
import { Input, Modal } from '@monavate/ui-core';
import {
    closeWebhookEditModal,
    closeWebhookCreateModal,
    retrieveWebhookData,
} from '@/reduxStore/slices/webhookAndApiKeysSlice';
import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { webhookSchema } from '@/helpers/schemaValidations';
import { webhookApi } from '@/services/rest/apiInstances';
import { addNotification } from '@/reduxStore/slices/systemNotificationSlice';
import { RootState, useAppSelector } from '@/reduxStore/rootReducer';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

const defaultPApiKeyObject = {
    uri: '',
    name: '',
    value: '',
};

interface GenerateApiKeyModalProps {
    isOpen: boolean;
    isEditModal: boolean;
}

const WebhookEditModal: React.FC<GenerateApiKeyModalProps> = ({ isOpen, isEditModal }) => {
    const dispatch = useDispatch();
    const webhookData = useAppSelector((state: RootState) => state.webhookAndApiKeys.webhookData);
    const constructDefaultValueForForm = () => {
        if (isOpen && webhookData && webhookData.uri) {
            return {
                uri: webhookData.uri,
                name: webhookData.header.name,
                value: webhookData.header.value,
            };
        } else {
            return defaultPApiKeyObject;
        }
    };
    const {
        formState: { errors, isValid, isDirty },
        register,
        getValues,
    } = useForm({
        // @ts-ignore
        resolver: yupResolver(webhookSchema),
        mode: 'all',
        defaultValues: constructDefaultValueForForm(),
    });

    const onCancelButtonClickHandler = () => {
        if (isEditModal) {
            dispatch(closeWebhookEditModal());
        } else {
            dispatch(closeWebhookCreateModal());
        }
    };
    const onSaveButtonClickHandler = async () => {
        const values = getValues();
        const constructRequestObject = {
            uri: values.uri,
            header: {
                value: values.value,
                name: values.name,
            },
        };
        try {
            // @ts-ignore
            const options = buildClientApiConfig();
            const request = await webhookApi.updateWebhook(
                { webhook: constructRequestObject },
                options,
            );
            // @ts-ignore
            dispatch(retrieveWebhookData());
            onCancelButtonClickHandler();
            const message = isEditModal ? 'Webhook was edited' : 'Webhook was added';
            dispatch(addNotification({ message: message, status: 'success' }));
        } catch (e: any) {
            const errorMessage = e?.response?.data?.message;
            dispatch(addNotification({ message: errorMessage, status: 'error' }));
        }
    };

    return (
        <Modal
            width={648}
            open={isOpen}
            title={isEditModal ? 'Edit webhook' : 'Setup webhook'}
            onClose={onCancelButtonClickHandler}
            onSubmit={onSaveButtonClickHandler}
            submitTitle={isEditModal ? 'Save' : 'Setup'}
            disabledSubmit={!isValid || !isDirty}
            contentGap={8}
        >
            <Input
                style={{ width: '100%' }}
                placeholder={'Text'}
                label="URL"
                required
                register={() => register('uri')}
                error={errors.uri?.message}
            />
            <Input
                style={{ width: '100%' }}
                placeholder={'Text'}
                required
                label="Header name"
                register={() => register('name')}
                error={errors.name?.message}
            />
            <Input
                style={{ width: '100%' }}
                placeholder={'Text'}
                required
                label="Header value"
                register={() => register('value')}
                error={errors.value?.message}
            />
        </Modal>
    );
};

export default WebhookEditModal;
